/**
 * Google Calendar Sync for Appointment System
 * Uses backend API at werkbonnen.justfire.nl
 *
 * Features:
 * - Fetches blocked slots from Google Calendar
 * - Creates appointments in Google Calendar
 * - Works for ALL visitors (no client-side auth needed)
 */

class GoogleCalendarSync {
  constructor() {
    // Backend API endpoints
    this.API_BASE = 'https://werkbonnen.justfire.nl/google-api';
    this.GET_SLOTS_URL = `${this.API_BASE}/get-slots.php`;
    this.BOOK_URL = `${this.API_BASE}/book-appointment.php`;

    // State
    this.blockedSlots = new Set();
    this.lastSync = null;
    this.syncInterval = null;
    this.initialized = false;
    this.isLoading = false;

    // Settings
    this.SYNC_INTERVAL_MS = 2 * 60 * 1000; // Sync every 2 minutes (faster)
    this.debug = true;

    // Callbacks for when sync completes
    this.onSyncCallbacks = [];
  }

  /**
   * Register callback for when sync completes
   */
  onSync(callback) {
    this.onSyncCallbacks.push(callback);
  }

  /**
   * Trigger all sync callbacks
   */
  triggerSyncCallbacks() {
    this.onSyncCallbacks.forEach(cb => {
      try { cb(this.blockedSlots); } catch(e) { console.error(e); }
    });
  }

  log(...args) {
    if (this.debug) {
      console.log('[GoogleCalendarSync]', ...args);
    }
  }

  /**
   * Initialize the sync system
   */
  async init() {
    this.log('Initializing Google Calendar sync...');
    this.log('API endpoint:', this.GET_SLOTS_URL);

    // Initial sync
    await this.syncBlockedSlots();

    // Set up periodic sync
    this.setupAutoSync();

    this.initialized = true;
    this.log('Google Calendar sync initialized');
  }

  /**
   * Fetch blocked slots from backend
   */
  async syncBlockedSlots() {
    // Prevent concurrent syncs
    if (this.isLoading) {
      this.log('Sync already in progress, skipping...');
      return false;
    }

    this.isLoading = true;
    this.log('========== SYNCING BLOCKED SLOTS ==========');

    try {
      const response = await fetch(this.GET_SLOTS_URL, {
        cache: 'no-store' // Always get fresh data
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.message || 'Unknown error');
      }

      this.log(`Fetched ${data.count} blocked slots from Google Calendar`);

      // Update blocked slots
      this.blockedSlots.clear();
      data.blockedSlots.forEach(slot => {
        this.blockedSlots.add(slot);
      });

      // Log blocked slots (only first 10 to avoid spam)
      if (data.blockedSlots.length > 0) {
        this.log('Blocked slots:', data.blockedSlots.slice(0, 10));
        if (data.blockedSlots.length > 10) {
          this.log(`... and ${data.blockedSlots.length - 10} more`);
        }
      }

      this.lastSync = new Date();
      this.isLoading = false;

      // Trigger callbacks (this updates the dropdown)
      this.triggerSyncCallbacks();

      // Also directly update dropdown if available
      this.updateDropdown();

      this.log('========== SYNC COMPLETE ==========');
      return true;

    } catch (error) {
      this.isLoading = false;
      this.log('ERROR syncing blocked slots:', error.message);
      console.error('[GoogleCalendarSync] Full error:', error);
      return false;
    }
  }

  /**
   * Update the appointment dropdown with blocked slots
   */
  updateDropdown() {
    const dateSelect = document.querySelector('select[name="select-3"]');
    if (!dateSelect) {
      this.log('Date dropdown not found');
      return;
    }

    let hiddenCount = 0;
    Array.from(dateSelect.options).forEach(option => {
      if (this.blockedSlots.has(option.value)) {
        option.style.display = 'none';
        option.disabled = true;
        hiddenCount++;
      } else {
        option.style.display = '';
        option.disabled = false;
      }
    });

    this.log(`Updated dropdown: ${hiddenCount} slots hidden`);
  }

  /**
   * Book an appointment via backend
   */
  async bookAppointment(slot, customerData) {
    this.log('========== BOOKING APPOINTMENT ==========');
    this.log('Slot:', slot);
    this.log('Customer:', customerData);

    try {
      const response = await fetch(this.BOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          slot: slot,
          // Customer info
          aanhef: customerData.aanhef || '',
          voornaam: customerData.voornaam || '',
          achternaam: customerData.achternaam || '',
          email: customerData.email || '',
          phone: customerData.phone || '',
          // Address
          adres: customerData.adres || '',
          huisnummer: customerData.huisnummer || '',
          toevoeging: customerData.toevoeging || '',
          postcode: customerData.postcode || '',
          woonplaats: customerData.woonplaats || '',
          land: customerData.land || '',
          // Appointment details
          service: customerData.service || 'Algemeen'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.message || 'Unknown error');
      }

      this.log('SUCCESS! Appointment created');
      this.log('Event ID:', data.event.id);
      this.log('Event link:', data.event.link);
      console.log('[GoogleCalendarSync] Event details:', data.event);

      // Add to local blocked slots
      this.blockedSlots.add(slot);

      // Update dropdown immediately
      this.updateDropdown();

      this.log('========== BOOKING COMPLETE ==========');
      return data;

    } catch (error) {
      this.log('ERROR booking appointment:', error.message);
      console.error('[GoogleCalendarSync] Full error:', error);
      return { error: true, message: error.message };
    }
  }

  /**
   * Set up automatic syncing
   */
  setupAutoSync() {
    // Clear existing interval
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }

    // Sync periodically
    this.syncInterval = setInterval(() => {
      this.log('Auto-sync triggered');
      this.syncBlockedSlots();
    }, this.SYNC_INTERVAL_MS);

    // Sync when window regains focus
    window.addEventListener('focus', () => {
      const timeSinceLastSync = Date.now() - (this.lastSync?.getTime() || 0);
      if (timeSinceLastSync > 60000) { // Only if last sync was >1 min ago
        this.log('Window focused - syncing...');
        this.syncBlockedSlots();
      }
    });

    this.log(`Auto-sync set up (every ${this.SYNC_INTERVAL_MS / 1000 / 60} minutes)`);
  }

  /**
   * Check if a slot is blocked
   */
  isSlotBlocked(slot) {
    return this.blockedSlots.has(slot);
  }

  /**
   * Get status info
   */
  getStatus() {
    return {
      initialized: this.initialized,
      blockedSlotsCount: this.blockedSlots.size,
      lastSync: this.lastSync?.toISOString() || null,
      apiEndpoint: this.GET_SLOTS_URL
    };
  }
}

// Initialize immediately
window.googleCalendarSync = new GoogleCalendarSync();

// Start syncing right away (don't wait for anything)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.googleCalendarSync.init();
  });
} else {
  // DOM already loaded, init immediately
  window.googleCalendarSync.init();
}

// Also sync again after full page load (forms might load late)
window.addEventListener('load', () => {
  setTimeout(() => {
    window.googleCalendarSync.updateDropdown();
  }, 500);
});

// Watch for dropdown to appear (for dynamically loaded forms)
const dropdownObserver = new MutationObserver(() => {
  const dateSelect = document.querySelector('select[name="select-3"]');
  if (dateSelect && window.googleCalendarSync.initialized) {
    window.googleCalendarSync.updateDropdown();
  }
});
dropdownObserver.observe(document.body, { childList: true, subtree: true });

// Debug interface
window.debugGoogleCalendar = {
  sync: () => window.googleCalendarSync.syncBlockedSlots(),
  blockedSlots: () => Array.from(window.googleCalendarSync.blockedSlots),
  status: () => {
    const status = window.googleCalendarSync.getStatus();
    console.log('========== GOOGLE CALENDAR STATUS ==========');
    console.log('Initialized:', status.initialized);
    console.log('Blocked slots:', status.blockedSlotsCount);
    console.log('Last sync:', status.lastSync);
    console.log('API endpoint:', status.apiEndpoint);
    console.log('============================================');
    return status;
  },
  book: (slot, name, email, phone) => window.googleCalendarSync.bookAppointment(slot, {
    name: name || 'Test',
    email: email || 'test@example.com',
    phone: phone || '0612345678',
    service: 'Test'
  })
};
